const PREFERENCE_KEY = 'gamma.passkey-login.v1';
const LAST_USED_CREDENTIAL_KEY = 'gamma.passkey-login.last-used-credential.v1';
const BASE64URL_CREDENTIAL_ID = /^[A-Za-z0-9_-]{1,2048}$/;
const BASE64URL_USER_HANDLE = /^[A-Za-z0-9_-]{1,256}$/;

export type RememberedPasskeyCredential = {
  id: string;
  userHandle: string;
};

const isRememberedCredential = (value: unknown): value is RememberedPasskeyCredential =>
  typeof value === 'object' &&
  value !== null &&
  'id' in value &&
  typeof value.id === 'string' &&
  BASE64URL_CREDENTIAL_ID.test(value.id) &&
  'userHandle' in value &&
  typeof value.userHandle === 'string' &&
  BASE64URL_USER_HANDLE.test(value.userHandle);

const removeStoredValue = (key: string): void => {
  try {
    localStorage.removeItem(key);
  } catch {
    return;
  }
};

// sessionStorage survives reloads, so suppression stays in module memory and expires with this document.
let passkeyPromptHandled = false;

export const hasPasskeyLoginPreference = (): boolean => {
  try {
    return localStorage.getItem(PREFERENCE_KEY) === '1';
  } catch {
    return false;
  }
};

export const rememberPasskeyLogin = (): void => {
  try {
    localStorage.setItem(PREFERENCE_KEY, '1');
  } catch {
    return;
  }
};

export const forgetPasskeyLogin = (): void => {
  passkeyPromptHandled = false;
  removeStoredValue(PREFERENCE_KEY);
  forgetLastUsedPasskeyCredential();
};

export const getLastUsedPasskeyCredential = (): RememberedPasskeyCredential | null => {
  try {
    const stored = localStorage.getItem(LAST_USED_CREDENTIAL_KEY);
    if (!stored) {
      return null;
    }
    const credential: unknown = JSON.parse(stored);
    return isRememberedCredential(credential) ? credential : null;
  } catch {
    return null;
  }
};

export const rememberLastUsedPasskeyCredential = (
  credential: RememberedPasskeyCredential,
): void => {
  if (!isRememberedCredential(credential)) {
    return;
  }
  try {
    localStorage.setItem(LAST_USED_CREDENTIAL_KEY, JSON.stringify(credential));
  } catch {
    return;
  }
};

export const forgetLastUsedPasskeyCredential = (): void => {
  removeStoredValue(LAST_USED_CREDENTIAL_KEY);
};

export const suppressPasskeyPrompt = (): void => {
  passkeyPromptHandled = true;
};

export const takePasskeyPrompt = (): boolean => {
  if (!hasPasskeyLoginPreference() || passkeyPromptHandled) {
    return false;
  }
  passkeyPromptHandled = true;
  return true;
};
